import { useMemo, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import { CATEGORIES } from "../utils/Categories";

/**
 * Lets users select which products are:
 *  - Delivery + manual approval
 *  - Delivery + no manual approval
 *  - Takeaway + manual approval
 *  - Takeaway + no manual approval
 *
 * Uses shipping.flags via the useShippingAssignments hook.
 */
export default function FulfillmentSelectionPanel({ products = [], shipping, onSaveAll }) {
  const [mode, setMode] = useState("delivery_manual"); // delivery_manual | delivery_auto | takeaway_manual | takeaway_auto
  const [q, setQ] = useState("");

  const {
    flags = {},
    assignments = {},
    busy,
    error,
    validateBusinessRules,
    setChannelFor,
    setChannelForMany,
  } = shipping;
  const norm = (s) => (s || "").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const nQ = norm(q);

  const productList = useMemo(() => {
    return (products || [])
      .filter((p) => !nQ || norm(p.name).includes(nQ))
      .map((p) => ({
        ...p,
        mainCategory: getMainCategoryName(p?.category || []),
      }));
  }, [products, nQ]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of productList) {
      const key = p.mainCategory || "Uncategorized";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries()); // [ [category, products[]], ... ]
  }, [productList]);
  const live = useMemo(
    () => validateBusinessRules ? validateBusinessRules(products) : { ok: true, details: {} },
    // depend on flags/assignments so the banner updates instantly
    [validateBusinessRules, products, flags, assignments]
  );
  const missingFulfillmentCount = live?.details?.missingFulfillment?.length || 0;
 
  const allIds = productList.map((p) => String(p.id));

  const isSelected = (pid) => {
    const f = flags[String(pid)] || {};
    switch (mode) {
      case "delivery_manual":
        return !!(f.delivery && f.delivery_requires_approval);
      case "delivery_auto":
        return !!(f.delivery && !f.delivery_requires_approval);
      case "takeaway_manual":
        return !!(f.takeaway && f.takeaway_requires_approval);
      case "takeaway_auto":
        return !!(f.takeaway && !f.takeaway_requires_approval);
      default:
        return false;
    }
  };

  const channelForMode = mode.startsWith("delivery") ? "delivery" : "takeaway";
  const requiresApprovalForMode = mode.endsWith("manual");

  const allSelectedCount = allIds.filter((id) => isSelected(id)).length;
  const allSelected = allSelectedCount === allIds.length && allIds.length > 0;
  const someSelected = allSelectedCount > 0 && allSelectedCount < allIds.length;

  const toggleAll = (on) => {
    setChannelForMany(allIds, channelForMode, on, requiresApprovalForMode);
  };

  const toggleCategory = (ids, on) => {
    setChannelForMany(ids, channelForMode, on, requiresApprovalForMode);
  };

  const headerLabel = (() => {
    if (mode === "delivery_manual") return "Delivery — requires manual approval";
    if (mode === "delivery_auto") return "Delivery — no manual approval";
    if (mode === "takeaway_manual") return "Takeaway — requires manual approval";
    return "Takeaway — no manual approval";
  })();

  return (
    <div className="grid md:grid-cols-[260px,1fr] gap-4">
      {/* Left: mode picker */}
      <aside className="rounded-xl border bg-white overflow-hidden">
        <div className="p-2 border-b">
          <div className="text-sm font-medium">Fulfillment</div>
        </div>
        <ul className="text-sm">
          {[
            ["delivery_manual", "Delivery · manual approval"],
            ["delivery_auto", "Delivery · no approval"],
            ["takeaway_manual", "Takeaway · manual approval"],
            ["takeaway_auto", "Takeaway · no approval"],
          ].map(([val, label]) => {
            const active = mode === val;
            return (
              <li key={val}>
                <button
                  type="button"
                  onClick={() => setMode(val)}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${active ? "bg-indigo-50" : ""}`}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Right: selection list */}
      <section className="rounded-xl border bg-white overflow-hidden">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b bg-white p-3">
          <div className="flex flex-col">
            <div className="text-sm font-medium">{headerLabel}</div>
            <div className="mt-1 flex items-center gap-2 text-sm">
              <TriStateCheckbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onChange={toggleAll}
              />
              <span>Apply to all products</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="border rounded-lg px-2 py-1 flex items-center gap-2">
              <Search size={16} className="text-gray-400" />
              <input
                className="w-48 outline-none text-sm"
                placeholder="Filter products…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                inputMode="search"
              />
            </div>

            <button
              disabled={busy}
              onClick={onSaveAll}
              className="btn-primary rounded-xl flex items-center gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check size={16} />}
              Save
            </button>
          </div>
        </div>

        {error && <p className="text-red-600 px-3 py-2">{error}</p>}
        {missingFulfillmentCount > 0 && (
          <div className="mx-3 my-2 rounded-md bg-red-50 text-red-700 text-xs p-2">
            {missingFulfillmentCount} product{missingFulfillmentCount > 1 ? "s" : ""} have no fulfillment.
            Choose <strong>Delivery</strong> and/or <strong>Takeaway</strong>.
          </div>
        )}
        <div className="divide-y">
          {grouped.map(([cat, items]) => {
            const ids = items.map((i) => String(i.id));
            const selectedInCat = ids.filter((id) => isSelected(id)).length;
            const catAll = selectedInCat === ids.length && ids.length > 0;
            const catSome = selectedInCat > 0 && selectedInCat < ids.length;

            return (
              <div key={cat}>
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                  <div className="flex items-center gap-2 text-sm">
                    <TriStateCheckbox
                      checked={catAll ? true : catSome ? "indeterminate" : false}
                      onChange={(on) => toggleCategory(ids, on)}
                    />
                    <span className="font-medium">{cat}</span>
                    <span className="text-xs text-gray-500">({items.length})</span>
                  </div>
                  <div className="text-xs text-gray-500">Select all in category</div>
                </div>

                <ul>
                  {items.map((p) => {
                    const pid = String(p.id);
                    const selected = isSelected(pid);

                    return (
                      <li key={pid} className="border-t">
                        <div className="flex items-center gap-3 px-3 py-2">
                          <label className="flex items-center gap-2 min-w-[240px]">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) =>
                                setChannelFor(pid, channelForMode, e.target.checked, requiresApprovalForMode)
                              }
                              aria-label={`Set ${headerLabel} for ${p.name}`}
                            />
                            <span className="truncate">{p.name}</span>
                          </label>

                          {/* Small status pills for context */}
                          <div className="ml-auto flex flex-wrap gap-2 text-[11px]">
                            <StatusPill
                              active={!!(flags[pid]?.delivery)}
                              label={`Delivery${flags[pid]?.delivery ? (flags[pid]?.delivery_requires_approval ? " · manual" : " · auto") : ""}`}
                            />
                            <StatusPill
                              active={!!(flags[pid]?.takeaway)}
                              label={`Takeaway${flags[pid]?.takeaway ? (flags[pid]?.takeaway_requires_approval ? " · manual" : " · auto") : ""}`}
                            />
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/* ---------- tiny bits ---------- */
function getMainCategoryName(pathIds = []) {
  if (!Array.isArray(pathIds) || !pathIds.length) return null;
  const topId = String(pathIds[0]);
  const root = (CATEGORIES || []).find((n) => String(n.id) === topId);
  return root?.name || null;
}

function TriStateCheckbox({ checked, onChange }) {
  // checked: true | false | "indeterminate"
  return (
    <input
      type="checkbox"
      ref={(el) => {
        if (el) el.indeterminate = checked === "indeterminate";
      }}
      checked={checked === true}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

function StatusPill({ active, label }) {
  return (
    <span
      className={`px-2 py-0.5 rounded-full border ${
        active ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-50 border-gray-200 text-gray-500"
      }`}
    >
      {label}
    </span>
  );
}
